"use client";
import { useEffect, useState } from "react";
import { AlertTriangle, Cake, User } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Drawer } from "@/components/ui/Drawer";
import { PatientDetailsBody } from "./PatientDetailsBody";
import { getInitials } from "@/lib/string";
import {
  PatientDetailsProvider,
  usePatientDetailsContext,
} from "../../context/PatientDetailsContext";

interface PatientDetailsDrawerProps {
  patientId: string | null;
  onClose: () => void;
  onLinkSent?: (patientId: string) => void;
}

function PatientDetailsDrawerInner({
  onClose,
  onLinkSent,
}: Omit<PatientDetailsDrawerProps, "patientId">) {
  const { patientId, patient, isLoading, isError } = usePatientDetailsContext();

  useEffect(() => {
    if (isError) onClose();
  }, [isError, onClose]);

  const [confirmAction, setConfirmAction] = useState<"remove" | null>(null);
  const [linkSent, setLinkSent] = useState(false);

  const fullName = patient
    ? `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim()
    : "";

  const handleSendLink = () => {
    setLinkSent(true);
    if (patient?.patientId) onLinkSent?.(patient.patientId);
  };

  const handleConfirmRemove = () => {
    setConfirmAction(null);
    // TODO: wire DELETE card API when endpoint is available
  };

  const handleClose = () => {
    setConfirmAction(null);
    setLinkSent(false);
    onClose();
  };

  const headerStart = patient ? (
    <div className="flex size-[46px] shrink-0 items-center justify-center rounded-[14px] bg-[linear-gradient(135deg,#1884F9,#619BFF)] shadow-[0_2px_8px_rgba(24,132,249,0.25)]">
      <span className="text-[15px] font-extrabold tracking-[0.02em] text-white">
        {getInitials(fullName)}
      </span>
    </div>
  ) : undefined;

  const headerTitle = patient ? (
    <p className="m-0 text-lg leading-[1.2] font-bold tracking-[-0.02em] text-[#111827]">
      {fullName}
    </p>
  ) : undefined;

  const headerSubtitle = patient ? (
    <p className="m-0 mt-[3px] flex items-center gap-2 text-[13px] font-medium text-[#6B7280]">
      <span className="inline-flex items-center gap-[3px]">
        <Cake className="size-3 text-[#9CA3AF]" strokeWidth={1.8} />
        {patient.dateOfBirth ?? ""}
      </span>
      <span className="inline-flex items-center gap-[3px] tabular-nums">
        <User className="size-3 text-[#9CA3AF]" strokeWidth={1.8} />
        {patient.mrn ?? ""}
      </span>
    </p>
  ) : undefined;

  return (
    <>
      <Drawer
        open={patientId !== null}
        onClose={handleClose}
        header={
          patient
            ? {
                start: headerStart,
                title: headerTitle,
                subtitle: headerSubtitle,
              }
            : undefined
        }
      >
        {isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        )}

        {patient && !isLoading && (
          <PatientDetailsBody linkSent={linkSent} onSendLink={handleSendLink} />
        )}
      </Drawer>

      {confirmAction === "remove" && patient && (
        <div className="animate-v5-overlay-fade-in-fast fixed inset-0 z-50 flex items-center justify-center">
          <div
            onClick={() => setConfirmAction(null)}
            className="absolute inset-0 bg-black/50"
          />
          <div className="animate-v5-dialog-in relative z-1 w-[400px] overflow-hidden rounded-[28px] bg-white px-7 pt-7 pb-6 shadow-[0_20px_60px_rgba(0,0,0,0.2)]">
            <div className="mb-4 flex size-11 items-center justify-center rounded-xl bg-[#FEF2F2]">
              <AlertTriangle
                className="size-5 text-[#DC2626]"
                strokeWidth={2}
              />
            </div>
            <h3 className="m-0 mb-2 text-base font-bold text-[#111827]">
              Delete card on file?
            </h3>
            <p className="m-0 mb-6 text-[13px] leading-[1.6] font-medium text-[#6B7280]">
              This will permanently remove{" "}
              <strong className="text-[#111827]">{fullName}&apos;s</strong> card
              ending in{" "}
              <strong className="text-[#111827]">
                {patient.cardOnFile?.last4}
              </strong>{" "}
              from file. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                className="flex-1 cursor-pointer rounded-[10px] border border-[#E5E7EB] bg-white py-[11px] text-[13px] font-semibold text-[#374151] transition-all duration-150 hover:bg-[#F9FAFB]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemove}
                className="flex-1 cursor-pointer rounded-[10px] border-0 bg-[#DC2626] py-[11px] text-[13px] font-semibold text-white transition-all duration-150 hover:bg-[#B91C1C]"
              >
                Delete Card
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function PatientDetailsDrawer({
  patientId,
  onClose,
  onLinkSent,
}: PatientDetailsDrawerProps) {
  return (
    <PatientDetailsProvider patientId={patientId}>
      <PatientDetailsDrawerInner onClose={onClose} onLinkSent={onLinkSent} />
    </PatientDetailsProvider>
  );
}
